import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useApp } from "../../context/AppState";
import { generateDynamicPopupState } from "./DynamicPopupStates";
import RenderPopupInstance from "./RenderPopupInstance";
import {
  getCartDrawerPlacement,
  isCartProductOfferPopup,
  isEffectivelyEmbeddedPopup,
} from "../../utils/cartDrawerRuntime";
import { getCartDrawerEmbedConfigForPlacement } from "../../utils/cartDrawerSlotSelectors";
import {
  getStaticCartDrawerSteps,
  isStaticCustomCartDrawer,
} from "../../constants/custom-cart-drawer-template";

function normalizePopupList(raw) {
  if (raw == null) return [];
  return Array.isArray(raw) ? raw : [raw];
}

const resolveActiveStepIndex = (popup, rawStep) => {
  const schema = Array.isArray(popup?.schema) ? popup.schema : [];
  if (!schema.length) return 0;

  const step = Number(rawStep);
  if (!Number.isFinite(step)) return 0;

  // Preferred: already zero-based and valid
  if (step >= 0 && step < schema.length) return step;

  // Backward compatibility: some payloads store step as 1-based
  const oneBasedIndex = step - 1;
  if (oneBasedIndex >= 0 && oneBasedIndex < schema.length) return oneBasedIndex;

  return 0;
};

function Popup({ popupValues }) {
  const { appState } = useApp();
  const [globalPopupStates, setGlobalPopupStates] = useState({});

  const popupsList = useMemo(() => {
    const list = normalizePopupList(popupValues);
    return list.map((popup) => {
      if (!isCartProductOfferPopup(popup)) return popup;

      const existingEmbed = popup?.generalSettings?.condition?.embed || {};
      const placement = getCartDrawerPlacement(popup);
      const placementEmbed = getCartDrawerEmbedConfigForPlacement(placement);
      if (!placementEmbed) return popup;

      // Cart drawer: placement drives slot (above/below). Keep other embed fields.
      return {
        ...popup,
        generalSettings: {
          ...(popup?.generalSettings || {}),
          condition: {
            ...(popup?.generalSettings?.condition || {}),
            embed: {
              ...existingEmbed,
              is_embedded: true,
              css_selector: placementEmbed.css_selector,
              position: placementEmbed.position,
            },
          },
        },
      };
    });
  }, [popupValues]);

  useEffect(() => {
    if (!popupsList?.length) {
      if (!appState?.is_preview_mode) {
        setGlobalPopupStates({});
      }
      return;
    }
    if (!appState?.is_preview_mode) {
      setGlobalPopupStates((prev) => {
        const next = {};
        for (const popup of popupsList) {
          const popupType = popup?.popUpType || popup?.template_type;
          const stateKey = `${popupType}_${popup?._id}`;
          const prevState = prev[stateKey];
          const isNewToList = !prevState;

          const dynamicState = generateDynamicPopupState(popup, false);
          let activeStep = resolveActiveStepIndex(
            popup,
            popup?.generalSettings?.condition?.trigger?.step
          );
          if (isStaticCustomCartDrawer(popup)) {
            const { triggerStepIndex, drawerStepIndex, hasTrigger } =
              getStaticCartDrawerSteps(popup);
            if (hasTrigger && triggerStepIndex != null) {
              activeStep = triggerStepIndex;
            } else if (drawerStepIndex != null) {
              activeStep = drawerStepIndex;
            }
          }

          next[stateKey] = {
            ...dynamicState,
            ...(prevState || {}),
            openPopup: isNewToList
              ? true
              : prevState.openPopup === false
                ? false
                : prevState.openPopup ?? true,
            triggerType: isNewToList ? "hybrid_trigger" : prevState.triggerType,
            triggeredAt: isNewToList ? Date.now() : prevState.triggeredAt,
            closedAt: prevState?.closedAt ?? null,
            activeStep,
          };
        }
        return next;
      });
    }
  }, [popupsList, appState?.is_preview_mode]);

  const loadAllPopupModel = useCallback(async (popupId) => {
    const popupData = popupsList.find(
      (popup) => popup._id === popupId && popup.is_active
    );

    if (popupData) {
      const { step } = popupData?.generalSettings?.condition?.embed || {};
      const isEmbedded = isEffectivelyEmbeddedPopup(popupData);

      const popupType = popupData?.popUpType || popupData?.template_type;
      setGlobalPopupStates((prevStates) => ({
        ...prevStates,
        [`${popupType}_${popupData._id}`]: {
          activeStep: isEmbedded
            ? resolveActiveStepIndex(popupData, step)
            : resolveActiveStepIndex(
                popupData,
                popupData?.generalSettings?.condition?.trigger?.step
              ),
          openPopup: true,
          isEmbeded: isEmbedded,
        },
      }));

      // await addAnalyticsEvent({
      //   eventType: analyticsEventTypes.OPEN,
      //   popupType: popupData.popUpType,
      //   popupId: popupData._id,
      //   ...(popupData?.campaign_Id && { campaignId: popupData?.campaign_Id }),
      // });
    }
  }, [popupsList]);

  useEffect(() => {
    const { is_preview_mode, activeStep } = appState || {};

    if (is_preview_mode && (popupsList?.[0]?.popUpType || popupsList?.[0]?.template_type)) {
      const popUpType = popupsList[0].popUpType || popupsList[0].template_type;
      const popUpId = popupsList[0]._id;

      // Use dynamic system for all templates, regardless of POPUP_CONFIG
      const dynamicState = generateDynamicPopupState(popupsList[0], true);
      setGlobalPopupStates((prev) => ({
        ...prev,
        [`${popUpType}_${popUpId}`]: {
          ...dynamicState,
          activeStep: resolveActiveStepIndex(popupsList[0], activeStep),
          openPopup: true // Always open in preview mode
        },
      }));
    }
  }, [appState?.is_preview_mode, appState?.activeStep, popupsList]);

  const handlePopupTrigger = useCallback((event) => {
    const { id } = event.detail;
    loadAllPopupModel(id);
  }, [loadAllPopupModel]);

  useEffect(() => {
    window.addEventListener('widget:trigger', handlePopupTrigger)

    return () => {
      window.removeEventListener('widget:trigger', handlePopupTrigger)
    }
  }, [handlePopupTrigger]);

  useEffect(() => {
    const resetStaticCartDrawerStep = () => {
      setGlobalPopupStates((prev) => {
        let changed = false;
        const next = { ...prev };

        popupsList.forEach((popup) => {
          if (!isStaticCustomCartDrawer(popup)) return;
          const { triggerStepIndex, drawerStepIndex, hasTrigger } =
            getStaticCartDrawerSteps(popup);
          const popupType = popup.popUpType || popup.template_type;
          const stateKey = `${popupType}_${popup._id}`;
          const current = next[stateKey];
          if (!current) return;

          const targetStep =
            hasTrigger && triggerStepIndex != null
              ? triggerStepIndex
              : drawerStepIndex ?? 0;
          if (current.activeStep === targetStep) return;

          changed = true;
          next[stateKey] = {
            ...current,
            activeStep: targetStep,
          };
        });

        return changed ? next : prev;
      });
    };

    window.addEventListener(
      "weupsell:static-cart-drawer-close",
      resetStaticCartDrawerStep
    );
    return () => {
      window.removeEventListener(
        "weupsell:static-cart-drawer-close",
        resetStaticCartDrawerStep
      );
    };
  }, [popupsList]);

  if (!Object.keys(globalPopupStates)?.length) return null;

  return (
    <>
      {popupsList.map((popupData, index) => {
        const popupId = popupData?._id;
        const popupType = popupData?.popUpType || popupData?.template_type;
        const popupState = globalPopupStates[`${popupType}_${popupId}`];
        
        return (
          <RenderPopupInstance
            key={`${popupType}_${popupId}_${index}`}
            popupState={popupState}
            popupData={popupData}
            setPopupState={setGlobalPopupStates}
          />
        );
      })}
    </>
  );
}

export default Popup;
